import type { RouteMapItem } from "../types/routes";
import { routeTitle } from "./routeName";
import { haversineDistanceM } from "./routeLineStyle";

// Маршрут из редактора, опубликованный пользователем (есть владелец), или
// курируемый EcoWay. Модерация публикации - tasks/user-route-publishing.md.
export type RouteSource = "curated" | "community";
export const routeSource = (r: RouteMapItem): RouteSource => (r.owner_user_id != null ? "community" : "curated");
export const hasBothSources = (items: RouteMapItem[]): boolean => new Set(items.map(routeSource)).size === 2;

// Фильтр сложности показывает только те уровни, что реально есть в данных
// (на проде пока ни одного - tasks/route-passport.md).
export const availableGrades = (items: RouteMapItem[]): number[] =>
  [...new Set(items.map((r) => r.technical_grade).filter((g): g is number => g != null))].sort((a, b) => a - b);

export const distanceFromKm = (r: RouteMapItem, origin: { lat: number; lon: number } | null): number | null => {
  if (!origin || r.start_lat == null || r.start_lon == null) return null;
  return haversineDistanceM(origin.lat, origin.lon, r.start_lat, r.start_lon) / 1000;
};

// Общие фильтры и сортировка маршрутов: список на карте (LeftPanel,
// MobileMapOverlay) и каталог «Маршруты» считают одинаково.
export type DurationBucket = "short" | "day" | "multi";
export interface RouteFilter {
  query?: string;
  grades?: number[];
  duration?: DurationBucket[];
  loop?: boolean | null;
  source?: RouteSource | null;
}
export type RouteSort = "recommended" | "nearest" | "distance" | "elevation" | "duration" | "name";

// ≤4 ч - «полдня», ≤10 ч - «целый день», дольше - многодневный.
export const durationBucket = (r: RouteMapItem): DurationBucket | null => {
  const m = r.estimated_duration_minutes;
  if (m == null) return null;
  if (m <= 240) return "short";
  if (m <= 600) return "day";
  return "multi";
};

// Кольцевой - старт и финиш совпадают. Лёгкий список отдаёт is_loop с сервера;
// без него - по геометрии, если она есть; иначе неизвестно.
export const isLoop = (r: RouteMapItem): boolean | null => {
  if (typeof r.is_loop === "boolean") return r.is_loop;
  const c = r.coordinates;
  if (!c || c.length < 2) return null;
  const a = c[0];
  const b = c[c.length - 1];
  return Math.abs(a[0] - b[0]) < 1e-4 && Math.abs(a[1] - b[1]) < 1e-4;
};

const searchText = (r: RouteMapItem) => {
  const tags = Array.isArray(r.tags) ? r.tags.join(" ") : (r.tags as unknown as string) || "";
  return `${r.name} ${tags}`.toLocaleLowerCase();
};

export const filterRoutes = (items: RouteMapItem[], f: RouteFilter): RouteMapItem[] => {
  const q = f.query?.trim().toLocaleLowerCase();
  return items.filter((r) => {
    if (q && !searchText(r).includes(q)) return false;
    if (f.grades?.length && !(r.technical_grade != null && f.grades.includes(r.technical_grade))) return false;
    if (f.duration?.length) {
      const b = durationBucket(r);
      if (!b || !f.duration.includes(b)) return false;
    }
    if (f.loop != null && isLoop(r) !== f.loop) return false;
    if (f.source && routeSource(r) !== f.source) return false;
    return true;
  });
};

const STATUS_ORDER: Record<string, number> = { active: 2, draft: 1, closed: 0 };

// Неизвестные значения - всегда в конце, в любом направлении сортировки.
const byNumber = (pick: (r: RouteMapItem) => number | null | undefined, dir: 1 | -1) => (a: RouteMapItem, b: RouteMapItem) => {
  const x = pick(a);
  const y = pick(b);
  if (x == null && y == null) return 0;
  if (x == null) return 1;
  if (y == null) return -1;
  return (x - y) * dir;
};

const byName = (a: RouteMapItem, b: RouteMapItem) => routeTitle(a.name).localeCompare(routeTitle(b.name), "ru");

// isSaved (необязательно) поднимает сохранённые наверх; origin нужен только
// для «nearest» - без него сортировка по названию.
export const sortRoutes = (
  items: RouteMapItem[],
  sort: RouteSort,
  isSaved?: (id: number) => boolean,
  origin?: { lat: number; lon: number } | null
): RouteMapItem[] => {
  const primary =
    sort === "distance" ? byNumber((r) => r.distance_km, 1)
    : sort === "elevation" ? byNumber((r) => r.elevation_gain_m, -1)
    : sort === "duration" ? byNumber((r) => r.estimated_duration_minutes, 1)
    : sort === "nearest" && origin ? byNumber((r) => distanceFromKm(r, origin), 1)
    : sort === "name" || sort === "nearest" ? byName
    : (a: RouteMapItem, b: RouteMapItem) =>
        (STATUS_ORDER[b.status ?? ""] ?? 0) - (STATUS_ORDER[a.status ?? ""] ?? 0) || byName(a, b);
  return [...items].sort((a, b) => {
    if (isSaved) {
      const diff = Number(isSaved(b.id)) - Number(isSaved(a.id));
      if (diff) return diff;
    }
    return primary(a, b);
  });
};

// Быстрые фильтры в поиске на карте: длительность (можно несколько) и
// «лёгкие» (T1–T2). Пустой выбор - без фильтра.
export type QuickFilterKey = DurationBucket | "easy";
export const QUICK_FILTER_KEYS: QuickFilterKey[] = ["short", "day", "multi", "easy"];

export const quickToFilter = (keys: QuickFilterKey[]): RouteFilter => {
  const filter: RouteFilter = {};
  const duration = keys.filter((k): k is DurationBucket => k !== "easy");
  if (duration.length) filter.duration = duration;
  if (keys.includes("easy")) filter.grades = [1, 2];
  return filter;
};
